import { useCallback, useEffect, useMemo, useState } from "react";
import { Check, ClipboardPlus, FileBadge, FileDown, KeyRound, PauseCircle, Pencil, Save, UserRoundCheck, UserRoundPlus, X } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import AppSidebar from "@/components/AppSidebar";
import { ClearancePacketLog } from "@/components/compliance/ClearancePacketLog";
import { ProviderCreateDialog } from "@/components/ProviderCreateDialog";
import { ProviderPortalAccessPanel } from "@/components/ProviderPortalAccessPanel";
import { ProviderWorkItemsPanel } from "@/components/ProviderWorkItemsPanel";
import { ProviderPortalControls } from "@/components/ProviderPortalControls";
import { ProviderProfileEditor } from "@/components/ProviderProfileEditor";
import { InactiveProvidersPanel, ProviderStatusDialog } from "@/components/ProviderStatusControls";
import { useAuth } from "@/context/AuthContext";
import { useHighlightParam } from "@/lib/useHighlightParam";
import { api, formatApiError } from "@/lib/api";

const emptyWork = {
  provider_id: "",
  client_id: "",
  title: "",
  description: "",
  due_date: "",
  start_date: "",
  end_date: "",
  evaluation_type: "",
};

const workTypes = {
  placement: { label: "Staffing placement", endpoint: "/provider-placements" },
  task: { label: "Client task", endpoint: "/client-tasks" },
};

export default function ProviderManagementPage() {
  const { user } = useAuth();
  const canGrantPortalAccess = user?.role === "system_administrator";
  const canManageInvoices = user?.role === "system_administrator";
  const canAddProviders = ["system_administrator", "hr"].includes(user?.role);
  const [showCreate, setShowCreate] = useState(false);
  const [workVersion, setWorkVersion] = useState(0);
  const [applications, setApplications] = useState([]);
  const [providers, setProviders] = useState([]);
  const [inactiveProviders, setInactiveProviders] = useState([]);
  const [clients, setClients] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [workType, setWorkType] = useState("placement");
  const [work, setWork] = useState(emptyWork);
  const [profileProviderId, setProfileProviderId] = useState("");
  const [portalProviderId, setPortalProviderId] = useState("");
  const [statusProviderId, setStatusProviderId] = useState("");
  const [packetLogProvider, setPacketLogProvider] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState("");
  useHighlightParam(!loading);

  const load = useCallback(async () => {
    try {
      const [pending, approved, inactive, clientRows, administration] = await Promise.all([
        api.get("/providers/applications"),
        api.get("/providers/approved"),
        api.get("/providers/inactive"),
        api.get("/providers/client-options"),
        canManageInvoices ? api.get("/data-administration") : Promise.resolve({ data: {} }),
      ]);
      setApplications(pending.data);
      setProviders(approved.data);
      setInactiveProviders(inactive.data);
      setClients(clientRows.data);
      setInvoices(administration.data.invoices || []);
      setError("");
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setLoading(false);
    }
  }, [canManageInvoices]);

  useEffect(() => {
    load();
  }, [load]);

  const profileTargets = useMemo(() => [...applications, ...providers], [applications, providers]);
  const profileProvider =
    profileTargets.find((provider) => provider.id === profileProviderId) || null;
  const portalProvider = providers.find((provider) => provider.id === portalProviderId) || null;
  const statusProvider = providers.find((provider) => provider.id === statusProviderId) || null;

  async function decide(providerId, decision) {
    try {
      await api.post(`/providers/${providerId}/${decision}`);
      setNotice(`Provider application ${decision === "approve" ? "approved" : "rejected"}.`);
      await load();
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }

  async function downloadResume(provider) {
    try {
      const response = await api.get(`/providers/${provider.id}/resume`, { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = provider.resume_filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }

  async function createWork(event) {
    event.preventDefault();
    try {
      await api.post(workTypes[workType].endpoint, work);
      setWork(emptyWork);
      setWorkVersion((value) => value + 1);
      setNotice(`${workTypes[workType].label} assigned and provider notification queued.`);
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }

  async function refreshProfile(updatedProvider) {
    setNotice(`Profile saved for ${updatedProvider.first_name} ${updatedProvider.last_name}.`);
    await load();
  }

  async function saveInvoice(event) {
    event.preventDefault();
    try {
      await api.put(`/data-administration/invoices/${editingInvoice.id}`, {
        status: editingInvoice.status,
        amount: Number(editingInvoice.amount),
        invoice_date: editingInvoice.invoice_date,
        description: editingInvoice.description || "",
      });
      setEditingInvoice(null);
      setNotice("Invoice updated.");
      await load();
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }

  return (
    <div className="app-shell" data-testid="provider-management-shell">
      <AppSidebar activeItem="Provider Management" />
      <div className="app-content">
        <AppHeader />
        <main
          className="dashboard-page provider-management-page"
          data-testid="provider-management-page"
        >
          <section className="page-intro">
            <div>
              <p className="eyebrow">PROVIDER OPERATIONS</p>
              <h1>Provider Management</h1>
              <p className="page-subtitle">
                Complete provider records internally, review access, and assign client work.
              </p>
            </div>
            {canAddProviders && (
              <button className="button button-primary" data-testid="add-provider-button" type="button" onClick={() => setShowCreate(true)}>
                <UserRoundPlus size={14} /> Add provider
              </button>
            )}
          </section>
          {showCreate && (
            <ProviderCreateDialog
              onClose={() => setShowCreate(false)}
              onCreated={async (created) => {
                setShowCreate(false);
                setNotice(`${created.first_name} ${created.last_name} added as an approved ${created.discipline}. ${canGrantPortalAccess ? 'Use "Portal access" on their row to invite them to sign in.' : "A System Administrator can enable their portal sign-in from the provider list."}`);
                await load();
              }}
            />
          )}
          {error && (
            <p className="form-message error-message" data-testid="provider-management-error">
              {error}
            </p>
          )}
          {notice && (
            <p className="form-message success-message" data-testid="provider-management-success">
              {notice}
            </p>
          )}

          {profileProvider && (
            <ProviderProfileEditor
              provider={profileProvider}
              onClose={() => setProfileProviderId("")}
              onUpdated={refreshProfile}
            />
          )}

          {portalProvider && (
            <ProviderPortalAccessPanel
              provider={portalProvider}
              onClose={() => setPortalProviderId("")}
              onGranted={async (updatedProvider, outcome) => {
                setNotice(
                  outcome?.invite_email_error
                    ? `Portal access enabled for ${updatedProvider.first_name} ${updatedProvider.last_name}, but the invitation email was not delivered. Copy the invite link below and send it directly.`
                    : `Portal access enabled for ${updatedProvider.first_name} ${updatedProvider.last_name}.${outcome?.invite_email_sent ? " Invitation email sent." : ""}`
                );
                await load();
              }}
            />
          )}

          {statusProvider && (
            <ProviderStatusDialog
              provider={statusProvider}
              onClose={() => setStatusProviderId("")}
              onChanged={async (outcome) => {
                setStatusProviderId("");
                const count = outcome.archived_request_ids.length;
                setNotice(
                  `${outcome.provider.first_name} ${outcome.provider.last_name} marked ${outcome.provider.approval_status.toLowerCase()}. ${count} request${count === 1 ? "" : "s"} and linked assignments moved to the board's Archived view (restorable).`
                );
                await load();
              }}
            />
          )}

          <section className="provider-management-grid">
            <div className="requests-table-panel" data-testid="provider-applications-panel">
              <div className="section-heading">
                <div>
                  <h2>Pending provider applications</h2>
                  <p>{applications.length} awaiting TAG review</p>
                </div>
              </div>
              {applications.map((provider) => (
                <ProviderApplicationRow
                  provider={provider}
                  key={provider.id}
                  onApprove={() => decide(provider.id, "approve")}
                  onDownload={() => downloadResume(provider)}
                  onEdit={() => setProfileProviderId(provider.id)}
                  onReject={() => decide(provider.id, "reject")}
                />
              ))}
              {loading && <p className="provider-empty-state list-loading" data-testid="provider-applications-loading">Loading…</p>}
              {!loading && !error && !applications.length && (
                <p className="provider-empty-state" data-testid="provider-applications-empty">
                  No provider applications are awaiting review.
                </p>
              )}
            </div>

            <form
              className="request-form-panel provider-work-form"
              data-testid="provider-work-form"
              onSubmit={createWork}
            >
              <div className="section-heading">
                <div>
                  <h2>Assign provider work</h2>
                  <p>Providers receive a secure work-queue notification.</p>
                </div>
              </div>
              <label>
                Work type
                <select
                  data-testid="provider-work-type-select"
                  value={workType}
                  onChange={(event) => setWorkType(event.target.value)}
                >
                  {Object.entries(workTypes).map(([value, item]) => (
                    <option key={value} value={value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Provider
                <select
                  data-testid="provider-work-provider-select"
                  value={work.provider_id}
                  onChange={(event) => setWork({ ...work, provider_id: event.target.value })}
                  required
                >
                  <option value="">Select approved provider</option>
                  {providers.map((provider) => (
                    <option key={provider.id} value={provider.id}>
                      {`${provider.first_name} ${provider.last_name} · ${provider.discipline || "Profile incomplete"}`}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Client
                <select
                  data-testid="provider-work-client-select"
                  value={work.client_id}
                  onChange={(event) => setWork({ ...work, client_id: event.target.value })}
                  required
                >
                  <option value="">Select client</option>
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Title
                <input
                  data-testid="provider-work-title-input"
                  value={work.title}
                  onChange={(event) => setWork({ ...work, title: event.target.value })}
                  required
                />
              </label>
              <label>
                Due date
                <input
                  data-testid="provider-work-due-date-input"
                  type="date"
                  value={work.due_date}
                  onChange={(event) => setWork({ ...work, due_date: event.target.value })}
                />
              </label>
              {workType === "placement" && (
                <>
                  <label>
                    Start date
                    <input
                      data-testid="provider-work-start-date-input"
                      type="date"
                      value={work.start_date}
                      onChange={(event) => setWork({ ...work, start_date: event.target.value })}
                    />
                  </label>
                  <label>
                    End date
                    <input
                      data-testid="provider-work-end-date-input"
                      type="date"
                      value={work.end_date}
                      onChange={(event) => setWork({ ...work, end_date: event.target.value })}
                    />
                  </label>
                </>
              )}
              <label className="provider-work-full">
                Details
                <textarea
                  data-testid="provider-work-description-input"
                  rows="4"
                  value={work.description}
                  onChange={(event) => setWork({ ...work, description: event.target.value })}
                />
              </label>
              <button className="button button-primary" data-testid="create-provider-work-button">
                <ClipboardPlus size={16} />
                Assign {workTypes[workType].label}
              </button>
            </form>
          </section>

          <ProviderWorkItemsPanel onError={setError} onNotice={setNotice} reloadKey={workVersion} />
          <ProviderPortalControls providers={providers} />
          {canManageInvoices && (
          <section className="requests-table-panel" data-testid="invoice-review-panel">
            <div className="section-heading">
              <div>
                <h2>Invoice review</h2>
                <p>Update submitted provider invoices without leaving Provider Management.</p>
              </div>
            </div>
            <div className="provider-approved-grid">
              {invoices.map((invoice) => (
                <article key={invoice.id} data-testid={`admin-invoice-${invoice.id}`}>
                  <strong>{invoice.invoice_number || "Invoice"}</strong>
                  <span>
                    {invoice.client_name} · ${invoice.amount}
                  </span>
                  <small>{invoice.status}</small>
                  <button
                    className="provider-profile-link"
                    data-testid={`edit-invoice-${invoice.id}`}
                    type="button"
                    onClick={() => setEditingInvoice(invoice)}
                  >
                    <Pencil size={14} /> Edit invoice
                  </button>
                </article>
              ))}
              {!invoices.length && (
                <p className="provider-empty-state">No provider invoices need review.</p>
              )}
            </div>
            {editingInvoice && (
              <form
                className="inline-edit-form"
                data-testid="invoice-editor"
                onSubmit={saveInvoice}
              >
                <label>
                  Status
                  <select
                    data-testid="edit-invoice-status-input"
                    value={editingInvoice.status}
                    onChange={(event) =>
                      setEditingInvoice({ ...editingInvoice, status: event.target.value })
                    }
                  >
                    <option>Submitted</option>
                    <option>Approved</option>
                    <option>Paid</option>
                    <option>Rejected</option>
                  </select>
                </label>
                <label>
                  Amount
                  <input
                    data-testid="edit-invoice-amount-input"
                    min="0.01"
                    step="0.01"
                    type="number"
                    value={editingInvoice.amount}
                    onChange={(event) =>
                      setEditingInvoice({ ...editingInvoice, amount: event.target.value })
                    }
                  />
                </label>
                <label>
                  Invoice date
                  <input
                    data-testid="edit-invoice-date-input"
                    type="date"
                    value={editingInvoice.invoice_date || ""}
                    onChange={(event) =>
                      setEditingInvoice({ ...editingInvoice, invoice_date: event.target.value })
                    }
                  />
                </label>
                <label className="inline-edit-full">
                  Description
                  <textarea
                    data-testid="edit-invoice-description-input"
                    rows="3"
                    value={editingInvoice.description || ""}
                    onChange={(event) =>
                      setEditingInvoice({ ...editingInvoice, description: event.target.value })
                    }
                  />
                </label>
                <button className="button button-primary" data-testid="save-invoice-button">
                  <Save size={16} /> Save invoice
                </button>
              </form>
            )}
          </section>
          )}
          <section
            className="requests-table-panel provider-approved-panel"
            data-testid="approved-providers-panel"
          >
            <div className="section-heading">
              <div>
                <h2>Approved providers</h2>
                <p>{providers.length} active network accounts</p>
              </div>
            </div>
            <div className="provider-approved-grid">
              {providers.map((provider) => (
                <article data-testid={`approved-provider-${provider.id}`} key={provider.id}>
                  <UserRoundCheck size={18} />
                  <strong>
                    {provider.first_name} {provider.last_name}
                  </strong>
                  <span>{provider.discipline || "Profile incomplete"}</span>
                  <small data-testid={`provider-credentials-${provider.id}`}>{provider.credentials || "No credential records on the Credentials page"}</small>
                  <small data-testid={`provider-email-${provider.id}`}>
                    {provider.email || "No sign-in email on file"}
                  </small>
                  <em
                    className={`portal-status portal-status-${(provider.portal_status || "")
                      .toLowerCase()
                      .replace(/\s+/g, "-")}`}
                    data-testid={`portal-status-${provider.id}`}
                  >
                    Portal: {provider.portal_status}
                  </em>
                  {provider.services_paused && (
                    <em className="status-chip status-inactive" data-testid={`services-paused-${provider.id}`}>Services paused: no active supervisor</em>
                  )}
                  <div className="provider-card-actions">
                    <button
                      className="provider-profile-link"
                      data-testid={`edit-provider-profile-${provider.id}`}
                      type="button"
                      onClick={() => setProfileProviderId(provider.id)}
                    >
                      <Pencil size={14} /> Manage profile
                    </button>
                    {canGrantPortalAccess && (
                      <button
                        className="provider-profile-link"
                        data-testid={`portal-access-${provider.id}`}
                        type="button"
                        onClick={() => setPortalProviderId(provider.id)}
                      >
                        <KeyRound size={14} /> Portal access
                      </button>
                    )}
                    <button
                      className="provider-profile-link"
                      data-testid={`provider-status-${provider.id}`}
                      type="button"
                      onClick={() => setStatusProviderId(provider.id)}
                    >
                      <PauseCircle size={14} /> Status
                    </button>
                    {["hr", "system_administrator"].includes(user?.role) && (
                      <button
                        className="provider-profile-link"
                        data-testid={`clearance-packets-${provider.id}`}
                        type="button"
                        onClick={() => setPacketLogProvider(provider)}
                      >
                        <FileBadge size={14} /> Clearance packets
                      </button>
                    )}
                  </div>
                </article>
              ))}
              {loading && <p className="provider-empty-state list-loading" data-testid="providers-loading">Loading…</p>}
              {!loading && !error && !providers.length && (
                <p className="provider-empty-state" data-testid="providers-empty">
                  Approve a provider application to begin assigning work.
                </p>
              )}
            </div>
          </section>
          {packetLogProvider && <ClearancePacketLog provider={packetLogProvider} onClose={() => setPacketLogProvider(null)} />}
          <InactiveProvidersPanel
            providers={inactiveProviders}
            onError={setError}
            onReactivated={async (provider) => {
              setNotice(`${provider.first_name} ${provider.last_name} returned to active. Restore their archived requests from the Master Staffing Board if needed.`);
              await load();
            }}
          />
        </main>
      </div>
    </div>
  );
}

function ProviderApplicationRow({ provider, onApprove, onDownload, onEdit, onReject }) {
  return (
    <div className="provider-application-row" data-testid={`provider-application-${provider.id}`}>
      <div>
        <strong>
          {provider.first_name} {provider.last_name}
        </strong>
        <span>{provider.discipline || "Professional profile pending TAG review"}</span>
        <small>
          {provider.email} · {provider.phone}
        </small>
      </div>
      <div className="provider-application-actions">
        <button
          className="button button-secondary"
          data-testid={`edit-pending-provider-${provider.id}`}
          type="button"
          onClick={onEdit}
        >
          <Pencil size={15} /> Complete profile
        </button>
        {provider.resume_uploaded && (
          <button
            aria-label={`Download CV for ${provider.first_name} ${provider.last_name}`}
            className="projection-icon-button"
            data-testid={`download-provider-resume-${provider.id}`}
            type="button"
            onClick={onDownload}
          >
            <FileDown size={15} />
          </button>
        )}
        <button
          className="button button-primary"
          data-testid={`approve-provider-${provider.id}`}
          type="button"
          onClick={onApprove}
        >
          <Check size={15} /> Approve
        </button>
        <button
          className="button provider-reject-button"
          data-testid={`reject-provider-${provider.id}`}
          type="button"
          onClick={onReject}
        >
          <X size={15} /> Reject
        </button>
      </div>
    </div>
  );
}
